import xmlDomUtils from '../../../utils/XmlDomUtils';
import interactionParserUtils from './InteractionParserUtils';
import { getComponentNodes } from '../itemBody';
import { getMediaModels } from '../MediaModels';

const ELEMENT_NODE = 1;

const choiceParser = {
    getFirstElementNodeFromFragment(xmlString) {
        const trimmedXmlString = xmlString ? xmlString.trim() : '';

        if (!trimmedXmlString.startsWith('<')) {
            return xmlDomUtils.getElementFromString(xmlString);
        }

        const fragmentRoot = xmlDomUtils.getElementFromString(`<div>${xmlString}</div>`);
        return Array.from(fragmentRoot?.childNodes || []).find(node => node.nodeType === ELEMENT_NODE);
    },

    parse(interactionElement, language, defaultLanguage, idPrefix, assessmentRunId, mediaInfo, supportedLanguages) {
        const attributes = xmlDomUtils.getAttributes(interactionElement);
        const alternatives = interactionParserUtils.parseListOfChildren(interactionElement, 'simpleChoice', language, defaultLanguage);
        const prompt = interactionParserUtils.getPrompt(interactionElement, language, defaultLanguage);
        const interactionType = attributes['inspera:type'];
        const shuffle = attributes.shuffle === 'true';
        const orientation = attributes.orientation;
        const maxChoices = Number.parseInt(attributes.maxChoices, 10);

        choiceParser.parseAlternatives(alternatives, language, defaultLanguage, idPrefix, assessmentRunId, mediaInfo, supportedLanguages);

        const hasAudioAlternatives = alternatives.some(alternative => alternative.isAudio);

        return {
            alternatives,
            hasAudioAlternatives,
            interactionType,
            maxChoices,
            orientation,
            prompt,
            shuffle,
        };
    },

    parseAlternatives(alternatives, language, defaultLanguage, idPrefix, assessmentRunId, mediaInfo, supportedLanguages) {
        alternatives.forEach((alternative) => {
            if (!xmlDomUtils.isDomParsableString(alternative.content)) {
                return;
            }
            let xmlElement = this.getFirstElementNodeFromFragment(alternative.content);
            if (xmlElement?.nodeType !== ELEMENT_NODE) {
                return;
            }

            const classList = xmlDomUtils.getClassList(xmlElement);
            if (classList.includes('LaTeX')) {
                alternative.content = this.replaceLaTeXElement(xmlElement);
            } else if (classList.includes('Audio')) {
                alternative.isAudio = true;
                const idPrefix2 = `${idPrefix}alt_${alternative.identifier}`;
                xmlElement = xmlDomUtils.getElementFromString(`<div>${this.replaceAudioEmptySpanElement(alternative.content)}</div>`);

                // backwards compatibility for alternative containing audio with no language support
                const audioElements = Array.from(xmlElement.childNodes)
                    .filter(node => node.nodeType === ELEMENT_NODE && (node.getAttribute('class') || '').includes('ia-audio'));
                if (audioElements.length === 1 && !audioElements[0].hasAttribute('lang') && language !== defaultLanguage) {
                    audioElements[0].setAttribute('lang', language);
                }

                alternative.mediaModels = getMediaModels(
                    idPrefix2, xmlElement, assessmentRunId, language, mediaInfo, defaultLanguage, supportedLanguages
                );
                getComponentNodes(xmlElement, idPrefix2, language, defaultLanguage);
                alternative.content = xmlDomUtils.stringify(xmlElement.childNodes);
            } else if (this.isImageAlternative(xmlElement)) {
                alternative.isImage = true;
                const idPrefix2 = `${idPrefix}alt_${alternative.identifier}`;
                let xmlString = `<div>${alternative.content}</div>`;

                if (xmlElement.nodeName === 'img') {
                    // backwards compatibility for alternative containing image with no language support
                    const langAttr = language !== defaultLanguage ? ` lang="${language}"` : '';
                    xmlString = `<div><div class="ia-image ia-media"${langAttr}>${alternative.content}</div></div>`;
                }

                xmlElement = xmlDomUtils.getElementFromString(xmlString);
                alternative.mediaModels = getMediaModels(
                    idPrefix2, xmlElement, assessmentRunId, language, mediaInfo, defaultLanguage, supportedLanguages
                );
                getComponentNodes(xmlElement, idPrefix2, language, defaultLanguage);
                alternative.content = xmlDomUtils.stringify(xmlElement.childNodes);
            }
        });

        return alternatives;
    },

    isImageAlternative(xmlElement) {
        return xmlElement.nodeName === 'img' || xmlElement.getElementsByTagName('img').length > 0;
    },

    replaceLaTeXElement(element) {
        return `<span><span class="math-tex">\\(${element.textContent}\\)</span></span>`;
    },

    // Empty span like this <span class="Audio"/> sometimes gives undesirable formatting
    // replace it with this <span class="Audio"></span>
    replaceAudioEmptySpanElement(content) {
        return content.replace(/<span class="Audio"\/>/g, '<span class="Audio"></span>');
    },
};

export default choiceParser;
